import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, Card, CardContent, styled, keyframes } from "@mui/material";
import { Star } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

// Rating data (static, doesn't need translation)
const testimonialRatings = [
  { id: 1, rating: 5 },
  { id: 2, rating: 4 },
  { id: 3, rating: 5 },
  { id: 4, rating: 5 },
  { id: 5, rating: 5 },
  { id: 6, rating: 5 },
  { id: 7, rating: 4 },
  { id: 8, rating: 5 },
  { id: 9, rating: 5 },
  { id: 10, rating: 4 },
  { id: 11, rating: 4 },
  { id: 12, rating: 5 },
  { id: 13, rating: 5 },
  { id: 14, rating: 5 },
  { id: 15, rating: 5 },
  { id: 16, rating: 4 },
  { id: 17, rating: 4 },
  { id: 18, rating: 5 },
  { id: 19, rating: 5 },
  { id: 20, rating: 5 },
  { id: 21, rating: 5 },
  { id: 22, rating: 4 },
  { id: 23, rating: 4 },
  { id: 24, rating: 5 },
  { id: 25, rating: 5 },
];

const ReviewsSection = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  height: "800px",
  background: "#29BC62",
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "none",
  "&::before": {
    content: '""',
    position: "absolute",
    top: "-64px",
    left: "calc(50% + 0.5px)",
    width: "2125px",
    height: "819px",
    backgroundImage: "url('/images/e188074a7c32402863a9e66388e05721b9968003.svg')",
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    transform: "translateX(-50%) rotate(180deg) scaleY(-1)",
    zIndex: 0,
  },
  [theme.breakpoints.down("lg")]: {
    height: "auto",
    minHeight: "600px",
    padding: "48px 0",
    overflow: "hidden",
  },
  [theme.breakpoints.down("md")]: {
    minHeight: "auto",
    padding: "32px 0",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "24px 0",
  },
}));

const ReviewsContent = styled(Box)(({ theme }) => ({
  position: "relative",
  zIndex: 1,
  width: "1110px",
  maxWidth: "100%",
  padding: "0 24px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "48px",
  background: "transparent",
  [theme.breakpoints.down("lg")]: {
    flexDirection: "column",
    gap: "32px",
    padding: "0 20px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "0 16px",
    gap: "24px",
  },
}));

const TestimonialColumnsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "24px",
  width: "531px",
  height: "800px",
  position: "relative",
  overflow: "hidden",
  padding: "0 8px",
  marginLeft: "-8px",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "194px",
    background: "linear-gradient(180deg, #27B25D 0%, rgba(39, 178, 93, 0) 100%)",
    zIndex: 2,
    pointerEvents: "none",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "100%",
    height: "194px",
    background: "linear-gradient(180deg, rgba(41, 188, 98, 0) 0%, #29BC62 100%)",
    zIndex: 2,
    pointerEvents: "none",
  },
  [theme.breakpoints.down("lg")]: {
    width: "100%",
    height: "700px",
    flexDirection: "row",
    gap: "16px",
    padding: "0 20px",
    marginLeft: "0",
    overflow: "hidden",
  },
  [theme.breakpoints.down("md")]: {
    height: "600px",
    gap: "12px",
    padding: "0 10px",
    overflow: "hidden",
  },
}));

// Magical floating animation for testimonials
const floatUp = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
  100% { transform: translateY(0px); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const sparkle = keyframes`
  0%, 100% { opacity: 0; transform: scale(0.8) rotate(0deg); }
  50% { opacity: 1; transform: scale(1) rotate(180deg); }
`;

const TestimonialColumn = styled(Box)<{ direction: "up" | "down" }>(({ direction, theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  width: "253.5px",
  willChange: "transform",
  "&:hover": {
    "& .testimonial-card": {
      animation: `${floatUp} 2s ease-in-out infinite`,
      animationDelay: "calc(var(--card-index) * 0.1s)",
    },
  },
  [theme.breakpoints.down("lg")]: {
    width: "50%",
    height: "100%",
  },
  [theme.breakpoints.down("md")]: {
    gap: "16px",
  },
}));

const TestimonialCard = styled(Card)(({ theme }) => ({
  margin: theme.spacing(0),
  borderRadius: "20px",
  background: "linear-gradient(135deg, #FFFFFF 0%, #FEFEFE 100%)",
  border: "1px solid #EFF0F0",
  boxShadow: "0px 2px 0px 0px #EFF0F0",
  minHeight: "200px",
  height: "auto",
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  marginBottom: "0px",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: "-2px",
    left: "-2px",
    right: "-2px",
    bottom: "-2px",
    borderRadius: "22px",
    opacity: 0,
    transition: "opacity 0.3s ease",
    zIndex: 0,
  },
  "&:hover": {
    transform: "translateY(-4px) scale(1.02)",
    zIndex: 10,
    "&::before": {
      opacity: 1,
    },
    "&::after": {
      opacity: 1,
      transform: "scale(1) rotate(180deg)",
      animation: `${sparkle} 2s ease-in-out infinite`,
    },
  },
  "& .MuiCardContent-root": {
    padding: "32px !important",
    paddingBottom: "32px !important",
    position: "relative",
    zIndex: 1,
    "&:last-child": {
      paddingBottom: "32px !important",
    },
  },
  [theme.breakpoints.down("lg")]: {
    minHeight: "200px",
    height: "auto",
    margin: "0 auto",
    maxWidth: "400px",
    width: "100%",
    "& .MuiCardContent-root": {
      padding: "24px !important",
      paddingBottom: "24px !important",
      "&:last-child": {
        paddingBottom: "24px !important",
      },
    },
  },
  [theme.breakpoints.down("md")]: {
    minHeight: "180px",
    fontSize: "13px",
    "& .MuiCardContent-root": {
      padding: "20px !important",
      paddingBottom: "20px !important",
      "&:last-child": {
        paddingBottom: "20px !important",
      },
    },
  },
}));

// Smooth infinite scroll component
const SmoothInfiniteColumn: React.FC<{
  direction: "up" | "down";
  testimonials: typeof testimonialRatings;
  speed?: number;
}> = ({ direction, testimonials: testimonialsData, speed = 1 }) => {
  const { t } = useTranslation();
  const columnRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const animationRef = useRef<number>();
  const translateY = useRef(0);
  const startTime = useRef(Date.now());

  const CARD_HEIGHT = 250; // Approximate height including gap
  const TOTAL_HEIGHT = testimonialsData.length * CARD_HEIGHT;

  useEffect(() => {
    const animate = () => {
      if (!columnRef.current || isHovered) {
        animationRef.current = requestAnimationFrame(animate);
        return;
      }

      const now = Date.now();
      const elapsed = (now - startTime.current) * speed * 0.02;

      if (direction === "up") {
        translateY.current = -(elapsed % TOTAL_HEIGHT);
      } else {
        translateY.current = -(TOTAL_HEIGHT - (elapsed % TOTAL_HEIGHT));
      }

      columnRef.current.style.transform = `translateY(${translateY.current}px)`;
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [direction, speed, isHovered, TOTAL_HEIGHT]);

  const renderTestimonials = () => {
    // Triple the testimonials for seamless loop
    const tripleTestimonials = [...testimonialsData, ...testimonialsData, ...testimonialsData];
    return tripleTestimonials.map((testimonial, index) => (
      <TestimonialCard
        key={`${direction}-${testimonial.id}-${index}`}
        className="testimonial-card"
        style={{ "--card-index": index } as React.CSSProperties}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <CardContent>
          <Box sx={{ display: "flex", gap: "4px", mb: { xs: 1.5, lg: 3 } }}>
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                sx={{
                  width: "16px",
                  height: "16px",
                  color: i < testimonial.rating ? "#FFAC07" : "#CFD1D5",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "scale(1.2) rotate(72deg)",
                    filter: "drop-shadow(0 0 4px #FFAC07)",
                  },
                }}
              />
            ))}
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: "8px", lg: "16px" } }}>
            <Typography
              component="p"
              sx={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 400,
                fontSize: { xs: "12px", lg: "14px" },
                lineHeight: "1.4",
                color: "text.fontDefault",
                margin: 0,
                padding: 0,
                display: "block",
                transition: "color 0.3s ease",
              }}
            >
              "{t(`common.customerReviews.testimonials.${testimonial.id}.text`)}"
            </Typography>
            <Typography
              component="p"
              sx={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 600,
                fontSize: { xs: "12px", lg: "14px" },
                lineHeight: "1.4",
                color: "#000000",
                margin: 0,
                padding: 0,
                display: "block",
                position: "relative",
                "&:hover::after": {
                  width: "100%",
                },
              }}
            >
              {t(`common.customerReviews.testimonials.${testimonial.id}.name`)}
            </Typography>
          </Box>
        </CardContent>
      </TestimonialCard>
    ));
  };

  return (
    <TestimonialColumn direction={direction}>
      <Box
        ref={columnRef}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          willChange: "transform",
        }}
      >
        {renderTestimonials()}
      </Box>
    </TestimonialColumn>
  );
};

const CustomerReviewsSection: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <ReviewsSection ref={sectionRef}>
      <ReviewsContent>
        <Box
          sx={{
            flex: 1,
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateX(0)" : "translateX(-30px)",
            transition: "all 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 600,
              fontSize: ["20px", "32px"],
              lineHeight: "1.5",
              color: "#FFFFFF",
              textAlign: i18n.language === "ar" ? "right" : "left",
              mb: 3,
              position: "relative",
            }}
          >
            {t('common.customerReviews.title')}
            <br />
            {t('common.customerReviews.titleLine2')}
          </Typography>
          <Typography
            sx={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 400,
              fontSize: ["14px", "18px"],
              lineHeight: "1.5",
              color: "#FFFFFF",
              textAlign: i18n.language === "ar" ? "right" : "left",
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateY(0)" : "translateY(20px)",
              transition: "all 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.3s",
            }}
          >
            {t('common.customerReviews.description')}
          </Typography>
        </Box>

        <TestimonialColumnsContainer
          sx={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateX(0)" : "translateX(30px)",
            transition: "all 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.2s",
          }}
        >
          <SmoothInfiniteColumn direction="up" testimonials={testimonialRatings} speed={1} />
          <SmoothInfiniteColumn direction="down" testimonials={testimonialRatings.slice().reverse()} speed={0.8} />
        </TestimonialColumnsContainer>
      </ReviewsContent>
    </ReviewsSection>
  );
};

export default CustomerReviewsSection;
